import { Box } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import Collaboration from '@tiptap/extension-collaboration';
import { Document } from '@tiptap/extension-document';
import { Placeholder } from '@tiptap/extension-placeholder';
import { AnyExtension, Editor, JSONContent, useEditor } from '@tiptap/react';
import clsx from 'clsx';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import xss from 'xss';
import type { XmlFragment as YXmlFragment } from 'yjs';

import { useContentLanguage } from 'shared/components/providers/ContentLanguageProvider';
import { RichTextExtraTools, RichTextTools, ToolbarType } from 'shared/types/RichTextToolbar';

import { useRichTextStyles } from './useRichTextStyles';
import { getDefaultExtensions } from '../../../utils/tiptap/convert';
import { PreserveBlockTypeOnSelectAll } from '../../../utils/tiptap/extensions/PreserveBlockTypeOnSelectAll';
import { TooltipNodeContext } from '../../../utils/tiptap/extensions/Tooltip/components/TooltipNode';
import { CustomTextAlign } from '../../../utils/tiptap/extensions/customTextAlign';
import { BlockTitleWidgetStyles, TextWidgetStyles, WidgetDefaultStyles } from '../../../utils/typographyStyles';
import { RichTextEditorFixed } from '../RichTextEditorFixed';
import { RichTextEditorInline } from '../RichTextEditorInline';

const defaultExtraExtensions: AnyExtension[] = [];

const HeadingDocument = Document.extend({
  content: 'heading+'
});

export type RichTextProps = {
  dataTestIdPrefix?: string;
  fragment?: YXmlFragment;
  placeholder?: string;
  htmlContent?: string;
  contentEditable?: boolean;
  /**
   * ProseMirror JSON
   */
  pmContent?: JSONContent;
  editable?: boolean;
  extraExtensions?: AnyExtension[];
  toolbarType?: ToolbarType;
  tools?: (RichTextTools | RichTextExtraTools)[];
  customControls?: ReactNode;
  className?: string;
  toolbarClassName?: string;
  withBorder?: boolean;
  expandHeight?: true;
  preserveWhitespace?: true | 'full';
  widgetStyles?: TextWidgetStyles | BlockTitleWidgetStyles;
  widgetDefaultStyles?: WidgetDefaultStyles;
  onChange?: (newValue: string) => void;
  setEditor?: (editor: Editor) => void;
  shouldHideTooltip?: boolean;
  tooltipClassName?: string;
  defaultTextAlignment?: 'left' | 'right' | 'center';
  tooltipWithinPortal?: boolean;
};

export function RichText(props: RichTextProps) {
  const {
    className,
    contentEditable = true,
    dataTestIdPrefix = '',
    defaultTextAlignment,
    editable = true,
    expandHeight,
    extraExtensions = defaultExtraExtensions,
    fragment,
    htmlContent,
    onChange,
    placeholder = '',
    pmContent,
    preserveWhitespace,
    setEditor,
    shouldHideTooltip,
    toolbarType = ToolbarType.None,
    tools = Object.values(RichTextTools),
    tooltipClassName,
    tooltipWithinPortal,
    widgetDefaultStyles,
    widgetStyles,
    withBorder = false
  } = props;

  const isCollaborative = Boolean(fragment);
  const isHtmlContent = Boolean(htmlContent && !fragment && !pmContent);

  const { contentDirection } = useContentLanguage();
  const defaultAlignment = defaultTextAlignment ?? (contentDirection === 'ltr' ? 'left' : 'right');
  const { classes, style: richTextStyle } = useRichTextStyles({
    expandHeight,
    widgetStyles,
    widgetDefaultStyles
  });

  const extensions = useMemo(() => {
    return [
      ...getDefaultExtensions({ useNativeHistory: !fragment }),
      ...extraExtensions,
      CustomTextAlign.configure({ types: ['heading', 'paragraph'], defaultAlignment }),
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: false
      }),
      PreserveBlockTypeOnSelectAll,
      ...(fragment
        ? [
            Collaboration.configure({
              fragment
            })
          ]
        : [])
    ];
  }, [defaultAlignment, extraExtensions, placeholder, fragment]);

  const editor = useEditor(
    {
      parseOptions: {
        preserveWhitespace
      },
      editable: editable ? contentEditable : false,
      extensions: tools.includes(RichTextExtraTools.TextLevelOnly) ? [...extensions, HeadingDocument] : extensions,
      onUpdate: ({ editor }) => {
        onChange?.(editor.getHTML());
      },
      content: isCollaborative
        ? undefined // Collaboration controls content
        : pmContent
        ? pmContent // ProseMirror JSON
        : htmlContent, // HTML
      immediatelyRender: false,
      shouldRerenderOnTransaction: true
    },
    [extensions, htmlContent, contentEditable, isCollaborative, pmContent]
  );

  const [isInitialRender, setIsInitialRender] = useState(true);

  useEffect(() => {
    setIsInitialRender(false);
  }, []);

  useEffect(() => {
    if (editable && setEditor && editor) {
      setEditor(editor);
    }
  }, [editable, editor, setEditor]);

  const { t } = useTranslation('widgets.richTextToolbar');

  const labels = useMemo(
    () => ({
      alignCenterControlLabel: t('alignCenter'),
      alignLeftControlLabel: t('alignLeft'),
      alignRightControlLabel: t('alignRight'),
      blockquoteControlLabel: t('blockquote'),
      boldControlLabel: t('bold'),
      clearFormattingControlLabel: t('clearFormatting'),
      codeControlLabel: t('code'),
      hrControlLabel: t('hr'),
      italicControlLabel: t('italic'),
      linkControlLabel: t('link'),
      strikeControlLabel: t('strikethrough'),
      underlineControlLabel: t('underline'),
      unlinkControlLabel: t('unlink')
    }),
    [t]
  );

  return (
    <TooltipNodeContext.Provider
      value={{
        shouldHide: shouldHideTooltip,
        isEditable: editable,
        className: tooltipClassName,
        withinPortal: tooltipWithinPortal
      }}
    >
      <Box style={richTextStyle} className={clsx(classes.wrapper, className, withBorder ? classes.richTextInline : '')}>
        <RichTextEditor
          data-testid={`${dataTestIdPrefix}-rich-text-editor`}
          classNames={{
            control: classes.control,
            toolbar: classes.toolbar,
            content: clsx(classes.content, { [classes.contentExpandHeight]: expandHeight }),
            root: classes.root,
            typographyStylesProvider: classes.typographyStylesProvider
          }}
          editor={editor}
          labels={labels}
        >
          {toolbarType === ToolbarType.Fixed ? (
            <RichTextEditorFixed {...props} />
          ) : toolbarType === ToolbarType.Inline ? (
            <RichTextEditorInline {...props} />
          ) : (
            <RichTextEditor.Content
              dangerouslySetInnerHTML={
                isHtmlContent && htmlContent && isInitialRender ? { __html: xss(htmlContent) } : undefined
              }
            />
          )}
        </RichTextEditor>
      </Box>
    </TooltipNodeContext.Provider>
  );
}
